import { ButtonPrimary, NovaButtonProps } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { selectAllFires } from "features/entity-caching/fires/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useRouter } from "pages"
import { FC, useCallback, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { selectOpenProjectDropdownFlightId } from "./redux/missions-table-selectors"
import { setOpenProjectDropdownFlightId } from "./redux/missions-table-slice"

export const OpenProjectButton = ({
    flightId,
    projectIds,
    SingleButtonComponent = ButtonPrimary,
    MultipleButtonComponent = ButtonPrimary,
}: {
    flightId: string
    projectIds: string[]
    SingleButtonComponent?: FC<NovaButtonProps>
    MultipleButtonComponent?: FC<NovaButtonProps>
}) => {
    const dispatch = useAppDispatch()
    const openProjectDropdownFlightId = useAppSelector(selectOpenProjectDropdownFlightId)
    const isOpen = openProjectDropdownFlightId === flightId
    const triggerRef = useRef<HTMLDivElement>(null)
    const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null)
    const router = useRouter("/workspace/:workspaceId")
    const allFires = useAppSelector(selectAllFires)

    const projects = useMemo(
        () =>
            projectIds.map((id) => ({
                id,
                name: allFires[id]?.name || id,
            })),
        [allFires, projectIds]
    )

    const navigateToProject = useCallback(
        (projectId: string, e: React.MouseEvent) => {
            e.stopPropagation()
            router.navigate({ path: "/project/:projectId", params: { projectId } })
        },
        [router]
    )

    const sections = useMemo(
        () => [
            projects.map((project) => ({
                title: project.name,
                onClick: () =>
                    router.navigate({
                        path: "/project/:projectId",
                        params: { projectId: project.id },
                    }),
            })),
        ],
        [projects, router]
    )

    if (projects.length === 0) {
        return null
    }

    if (projects.length === 1) {
        return (
            <SingleButtonComponent
                title="Open Project"
                onClick={(e) => navigateToProject(projects[0].id, e)}
                RightIcon={Icons.ArrowRight}
            />
        )
    }

    return (
        <div ref={triggerRef}>
            <MultipleButtonComponent
                title="Open Project"
                RightIcon={Icons.ArrowDropdown}
                rightIconClassName={classNames("transition-transform", {
                    "rotate-180": isOpen,
                })}
                LeftIcon={Icons.SubdirectoryArrowRight}
                onClick={(e) => {
                    e.stopPropagation()
                    if (!isOpen && triggerRef.current) {
                        const rect = triggerRef.current.getBoundingClientRect()
                        setDropdownPos({ top: rect.bottom, left: rect.left })
                    }
                    dispatch(setOpenProjectDropdownFlightId(isOpen ? null : flightId))
                }}
            />
            {isOpen &&
                dropdownPos &&
                createPortal(
                    <ContextMenu
                        sections={sections}
                        onBlur={() => dispatch(setOpenProjectDropdownFlightId(null))}
                        isFixedPosition
                        top={dropdownPos.top}
                        left={dropdownPos.left}
                    />,
                    document.getElementById("portal")!
                )}
        </div>
    )
}
